import { XHRRequestCanceler } from '../../../../Libs/xhr/XHRRequestCanceler';
import { Constants } from '../../../Constants';
import {
    IPointageDataService,
    PointageDataServiceName,
} from '../../../DataServices/PointageDataService';
import { Lazy } from '../../../Helpers/Lazy';
import { BaseApiDataResponse } from '../../../Models/ApiData';
import { ApiUrls } from '../../../Models/ApiUrls';
import {
    PointageListResponse,
    PointageSignatureListApiDataResponse,
    PointageSorts,
    PointageToAddResponse,
    SetPointageRequest,
} from '../../../Models/Pointage/Pointage';
import { DateUtils } from '../../../Utils/DateUtils';
import { ExportFileType } from '../../../Utils/ExportUtils';
import { IoC } from '../../ServicesContainer';
import { BaseReduxService } from '../Api/BaseReduxService';
import { DownloadServiceName, IDownloadService } from '../Download/DownloadService';
import { I18nServiceName, II18nService } from '../I18n/I18nService';

export interface IPointageService {
    getPointages: (
        requestParams: {
            week_date: Date;
            sorts?: PointageSorts;
            affaire_id?: string;
            compagnon_id?: string;
            charge_affaire_id?: string;
        },
        cts: XHRRequestCanceler
    ) => Promise<PointageListResponse>;

    setPointage: (
        requestParams: {
            day_date: Date;
            affaire_id?: string;
            compagnon_id: string;
            setPointageRequest: SetPointageRequest;
        },
        cts: XHRRequestCanceler
    ) => Promise<BaseApiDataResponse>;

    setComment: (
        requestParams: {
            day_date: Date;
            comment: string;
        },
        cts: XHRRequestCanceler
    ) => Promise<BaseApiDataResponse>;

    getPointagesSignature: (
        requestParams: {
            compagnon_id: string;
        },
        cts: XHRRequestCanceler
    ) => Promise<PointageSignatureListApiDataResponse>;

    setPointageSignatureComment: (
        requestParams: {
            compagnon_id: string;
            signature_id: string;
            comment: string;
        },
        cts: XHRRequestCanceler
    ) => Promise<BaseApiDataResponse>;

    exportPointages: (
        weekDate: Date,
        week_start_day_date: Date,
        week_end_day_date: Date,
        cts: XHRRequestCanceler
    ) => void;

    exportPointagesSignature: (
        compagnon_id: string,
        signature_id: string,
        pdfTitle: string,
        cts: XHRRequestCanceler
    ) => void;
    setValidationCheckMark: (
        requestParams: {
            is_validated: boolean;
            week_date: Date;
            affaire_id?: string;
            compagnon_id: string;
        },
        cts: XHRRequestCanceler
    ) => Promise<BaseApiDataResponse>;
    setValidationComment: (
        requestParams: {
            comment: string;
            week_date: Date;
            affaire_id?: string;
            compagnon_id: string;
        },
        cts: XHRRequestCanceler
    ) => Promise<BaseApiDataResponse>;

    getPointageToAdd: (cts: XHRRequestCanceler) => Promise<PointageToAddResponse>;
}

class PointageService extends BaseReduxService implements IPointageService {
    private readonly pointageDataService: Lazy<IPointageDataService>;
    private readonly i18nService: Lazy<II18nService>;
    private readonly downloadService: Lazy<IDownloadService>;

    constructor() {
        super();
        this.pointageDataService = IoC.getLazy<IPointageDataService>(PointageDataServiceName);
        this.i18nService = IoC.getLazy<II18nService>(I18nServiceName);
        this.downloadService = IoC.getLazy<IDownloadService>(DownloadServiceName);
    }

    public getPointageToAdd = async (cts: XHRRequestCanceler) => {
        try {
            const response = await this.pointageDataService.value().getPointageToAdd(cts);
            return Promise.resolve(response);
        } catch (err) {
            return Promise.reject(err);
        }
    };

    public setValidationCheckMark = async (
        requestParams: {
            week_date: Date;
            affaire_id?: string;
            compagnon_id: string;
            is_validated: boolean;
        },
        cts: XHRRequestCanceler
    ) => {
        try {
            const weekDateAsString = this.i18nService
                .value()
                .formatMessage('Pointage_WeekDateFormat', {
                    year: requestParams.week_date.getFullYear(),
                    month: DateUtils.formatTwoDigitsNumber(requestParams.week_date.getMonth() + 1),
                    day: DateUtils.formatTwoDigitsNumber(requestParams.week_date.getDate()),
                });
            const response = await this.pointageDataService
                .value()
                .setValidationCheckMark(
                    {
                        ...requestParams,
                        affaire_id:
                            requestParams.affaire_id ?? '00000000-0000-0000-0000-000000000000',
                        week_date: weekDateAsString,
                    },
                    cts
                );
            return Promise.resolve(response);
        } catch (err) {
            return Promise.reject(err);
        }
    };

    public setValidationComment = async (
        requestParams: {
            week_date: Date;
            affaire_id?: string;
            compagnon_id: string;
            comment: string;
        },
        cts: XHRRequestCanceler
    ) => {
        try {
            const weekDateAsString = this.i18nService
                .value()
                .formatMessage('Pointage_WeekDateFormat', {
                    year: requestParams.week_date.getFullYear(),
                    month: DateUtils.formatTwoDigitsNumber(requestParams.week_date.getMonth() + 1),
                    day: DateUtils.formatTwoDigitsNumber(requestParams.week_date.getDate()),
                });

            const response = this.pointageDataService.value().setValidationComment(
                {
                    ...requestParams,
                    affaire_id: requestParams.affaire_id ?? '00000000-0000-0000-0000-000000000000',
                    week_date: weekDateAsString,
                },
                cts
            );
            return Promise.resolve(response);
        } catch (err) {
            return Promise.reject(err);
        }
    };

    public getPointages = async (
        requestParams: {
            week_date: Date;
            sorts?: PointageSorts;
            affaire_id?: string;
            compagnon_id?: string;
            charge_affaire_id?: string;
        },
        cts: XHRRequestCanceler
    ) => {
        try {
            const { week_date: date } = requestParams;

            const weekDateAsString = this.i18nService
                .value()
                .formatMessage('Pointage_WeekDateFormat', {
                    year: date.getFullYear(),
                    month: DateUtils.formatTwoDigitsNumber(date.getMonth() + 1),
                    day: DateUtils.formatTwoDigitsNumber(date.getDate()),
                });
            const response = await this.pointageDataService.value().getPointages(
                {
                    weekDate: weekDateAsString,
                    sorts: requestParams.sorts,
                    affaire_id: requestParams.affaire_id,
                    compagnon_id: requestParams.compagnon_id,
                    charge_affaire_id: requestParams.charge_affaire_id,
                },
                cts
            );
            return Promise.resolve(response);
        } catch (err) {
            return Promise.reject(err);
        }
    };

    public setPointage = async (
        requestParams: {
            day_date: Date;
            affaire_id?: string;
            compagnon_id: string;
            setPointageRequest: SetPointageRequest;
        },
        cts: XHRRequestCanceler
    ) => {
        try {
            const { day_date: date } = requestParams;

            const weekDateAsString = this.i18nService
                .value()
                .formatMessage('Pointage_WeekDateFormat', {
                    year: date.getFullYear(),
                    month: DateUtils.formatTwoDigitsNumber(date.getMonth() + 1),
                    day: DateUtils.formatTwoDigitsNumber(date.getDate()),
                });

            const response = await this.pointageDataService.value().setPointage(
                {
                    weekDate: weekDateAsString,
                    affaire_id: requestParams.affaire_id ?? '00000000-0000-0000-0000-000000000000',
                    compagnon_id: requestParams.compagnon_id,
                    setPointageRequest: requestParams.setPointageRequest,
                },
                cts
            );
            return Promise.resolve(response);
        } catch (err) {
            return Promise.reject(err);
        }
    };

    public setComment = async (
        params: {
            day_date: Date;
            comment: string;
        },
        cts: XHRRequestCanceler
    ) => {
        const { day_date: date, comment } = params;
        try {
            const weekDateAsString = this.i18nService
                .value()
                .formatMessage('Pointage_WeekDateFormat', {
                    year: date.getFullYear(),
                    month: DateUtils.formatTwoDigitsNumber(date.getMonth() + 1),
                    day: DateUtils.formatTwoDigitsNumber(date.getDate()),
                });
            const response = await this.pointageDataService.value().setComment(
                {
                    weekDate: weekDateAsString,
                    comment,
                },
                cts
            );
            return Promise.resolve(response);
        } catch (err) {
            return Promise.reject(err);
        }
    };

    getPointagesSignature = async (
        requestParams: {
            compagnon_id: string;
        },
        cts: XHRRequestCanceler
    ) => {
        try {
            const response = await this.pointageDataService
                .value()
                .getPointagesSignature(requestParams, cts);
            return Promise.resolve(response);
        } catch (err) {
            return Promise.reject(err);
        }
    };

    setPointageSignatureComment = async (
        requestParams: {
            compagnon_id: string;
            signature_id: string;
            comment: string;
        },
        cts: XHRRequestCanceler
    ) => {
        try {
            const response = await this.pointageDataService
                .value()
                .setPointageSignatureComment(requestParams, cts);
            return Promise.resolve(response);
        } catch (err) {
            return Promise.reject(err);
        }
    };

    exportPointages = async (
        weekDate: Date,
        week_start_day_date: Date,
        week_end_day_date: Date,
        cts: XHRRequestCanceler
    ) => {
        try {
            const url =
                Constants.getRoot() +
                `/api/v1/back/pointages/weeks/${this.i18nService
                    .value()
                    .formatMessage('Pointage_WeekDateFormat', {
                        year: weekDate.getFullYear(),
                        month: DateUtils.formatTwoDigitsNumber(weekDate.getMonth() + 1),
                        day: DateUtils.formatTwoDigitsNumber(weekDate.getDate()),
                    })}/export`;
            const fileName = `Pointage - ${this.i18nService
                .value()
                .formatMessage('Pointage_WeekDateFormat', {
                    year: week_start_day_date.getFullYear(),
                    month: DateUtils.formatTwoDigitsNumber(week_start_day_date.getMonth() + 1),
                    day: DateUtils.formatTwoDigitsNumber(week_start_day_date.getDate()),
                })} - ${this.i18nService.value().formatMessage('Pointage_WeekDateFormat', {
                year: week_end_day_date.getFullYear(),
                month: DateUtils.formatTwoDigitsNumber(week_end_day_date.getMonth() + 1),
                day: DateUtils.formatTwoDigitsNumber(week_end_day_date.getDate()),
            })}`;

            this.downloadService.value().downloadFile(url, fileName, ExportFileType.EXCEL, cts);
        } catch (err) {
            Promise.reject(err);
        }
    };

    public exportPointagesSignature = (
        compagnon_id: string,
        signature_id: string,
        pdfTitle: string,
        cts: XHRRequestCanceler
    ) => {
        try {
            const url =
                Constants.getRoot() + ApiUrls.EXPORT_POINTAGES_SIGNTURE(compagnon_id, signature_id);
            this.downloadService.value().downloadFile(url, pdfTitle, ExportFileType.PDF, cts);
        } catch (err) {
            Promise.reject(err);
        }
    };
}

const PointageServiceName = 'PointageService';
export { PointageService, PointageServiceName };
